import { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Box from '@mui/material/Box';

type PropertyValueMultiLineInputProps = {
	value: any;
	onValueChange: (value: any) => void;
	label?: string;
	required?: boolean;
	readOnly?: boolean;
};

export default function PropertyValueMultiLineInput({ value, onValueChange, label, required, readOnly }: PropertyValueMultiLineInputProps) {
	const [multilineModalOpen, setMultilineModalOpen] = useState(false);
	const [modalValue, setModalValue] = useState('');
	const [inputValue, setInputValue] = useState(value ?? '');
	const isFocusedRef = useRef(false);

	useEffect(() => {
		if (!isFocusedRef.current) {
			setInputValue(value ?? '');
		}
	}, [value]);

	const handleOpenModal = () => {
		setModalValue(inputValue);
		setMultilineModalOpen(true);
	};

	const handleCloseModal = () => {
		setMultilineModalOpen(false);
	};

	const handleSaveModal = () => {
		setInputValue(modalValue);
		onValueChange?.(modalValue);
		setMultilineModalOpen(false);
	};

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%' }}>
				<TextField
					variant="standard"
					required={required}
					fullWidth
					multiline
					maxRows={3}
					value={inputValue}
					disabled={readOnly}
					onFocus={() => {
						isFocusedRef.current = true;
					}}
					onBlur={() => {
						isFocusedRef.current = false;
						if (inputValue !== (value ?? '')) {
							onValueChange(inputValue);
						}
					}}
					onChange={(e) => {
						setInputValue(e.target.value);
					}}
				/>
				<IconButton size="small" onClick={handleOpenModal} title="Open in full editor" disabled={readOnly}>
					<OpenInFullIcon fontSize="small" />
				</IconButton>
			</Box>
			<Dialog open={multilineModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
				<DialogTitle>{label || 'Edit Text'}</DialogTitle>
				<DialogContent>
					<TextField
						variant="outlined"
						fullWidth
						multiline
						rows={15}
						value={modalValue}
						onChange={(e) => setModalValue(e.target.value)}
						autoFocus
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal}>Cancel</Button>
					<Button onClick={handleSaveModal} variant="contained">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
